import React from 'react';
import withStyles from 'isomorphic-style-loader/withStyles';
import Container from '@material-ui/core/Container';
import Button from '@material-ui/core/Button';
import s from './Home.css';
import HeadMessage from '../../components/Common/HeadMessage/HeadMessage';
import Link from '../../components/Link';
import WidgetIcon from '../../components/WidgetIcon';
import ThemesShowcase from '../../components/ThemesShowcase';
import { getFeaturedWidgets, WIDGET_TYPES } from '../../data/widgets/featured';
import StagedLink from '../../components/StagedLink';
import { isReleased } from '../../data/widgets/release';
import reviews from '../../data/reviews';
import {
	CHROME_STORE_URL,
	FIREFOX_ADDON_URL,
	STORE_STATS,
} from '../../utils/seo';

/**
 * Дата отзыва в том же виде, в каком она стоит в Chrome Web Store.
 * Дата важна: отзыв без неё выглядит как цитата неизвестной свежести.
 */
function formatReviewDate(iso) {
	return new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-GB', {
		day: 'numeric',
		month: 'short',
		year: 'numeric',
		timeZone: 'UTC',
	});
}

class Home extends React.Component {
	render() {
		const featured = getFeaturedWidgets();

		return (
			<div className={s.root}>
				<Container>
					<HeadMessage>
						<h1>
							Rule your <span>new tab</span>
						</h1>
					</HeadMessage>

					<div className={s.hero}>
						<p className={s.tagline}>
							Your new tab page, built by you. Drag a countdown, a
							to-do list, a clock or a habit tracker onto any
							wallpaper and put them exactly where you want them.
						</p>

						<div className={s.buttons}>
							<Button
								href={CHROME_STORE_URL}
								color="primary"
								variant="contained"
								size="large"
							>
								Get New Tab Studio for free
							</Button>
						</div>

						<div className={s.stores}>
							<a href={CHROME_STORE_URL}>
								<img
									src="/chrome-web-store.png"
									alt="Available in the Chrome Web Store"
									width={150}
									height={46}
								/>
							</a>
							<a href={FIREFOX_ADDON_URL}>
								<img
									src="/firefox-addons.png"
									alt="Get New Tab Studio for Firefox"
									width={150}
									height={46}
								/>
							</a>
						</div>

						<p className={s.proof}>
							{STORE_STATS.users.toLocaleString('en-US')} users
							&middot; {STORE_STATS.ratingValue} out of 5 on the
							Chrome Web Store
						</p>
					</div>

					<ThemesShowcase />
				</Container>

				<div className={s.content}>
					<h2>What you can put on a new tab</h2>

					<p>
						The default new tab shows you a search box and a grid of
						places you’ve already been. Same page for everyone,
						tells you nothing about your day, and you look at it
						dozens of times before lunch. Seems a waste. So: a blank
						page, {WIDGET_TYPES} widgets, any wallpaper you like,
						and a layout you can hand to someone else.
					</p>

					<ul className={s.widgetLinks}>
						{featured.map(widget => (
							<li key={widget.slug}>
								<Link to={`/widgets/${widget.slug}`}>
									<WidgetIcon
										slug={widget.slug}
										category={widget.category}
									/>
									<span>{widget.name}</span>
								</Link>
							</li>
						))}
					</ul>

					<p>
						Version 4.0 redrew nearly all of them. Most widgets now
						come in several ready-made designs, more than 130
						between them, there are 22 fonts to pick from, and the
						interface speaks ten languages. It also brought new
						widgets: a{' '}
						<StagedLink to="/widgets/daily-focus">
							daily focus
						</StagedLink>{' '}
						for the one thing that matters today,{' '}
						<StagedLink to="/widgets/news">news</StagedLink>, a{' '}
						<StagedLink to="/widgets/facts">
							fact of the day
						</StagedLink>{' '}
						and a{' '}
						<StagedLink to="/widgets/chess-puzzle">
							chess puzzle
						</StagedLink>{' '}
						to solve between tabs.
					</p>

					<p className={s.allWidgets}>
						<Link to="/widgets">Browse the widget catalogue</Link>
					</p>

					{isReleased('/how-to-change-new-tab') && (
						<p className={s.allWidgets}>
							New to this?{' '}
							<Link to="/how-to-change-new-tab">
								How to change the new tab page in Chrome
							</Link>{' '}
							— including the parts you can do without any
							extension.
						</p>
					)}

					<h2>Three things people build with it</h2>

					<div className={s.columns}>
						<div className={s.column}>
							<h3>A deadline you can’t ignore</h3>
							<p>
								A{' '}
								<StagedLink to="/widgets/countdown">
									countdown
								</StagedLink>{' '}
								to the exam, the hand-in, the release, big
								enough that you see it before anything else.
								This is what most people install the extension
								for, and why the word is in its name on the
								Chrome store.
							</p>
						</div>

						<div className={s.column}>
							<h3>A working dashboard</h3>
							<p>
								<StagedLink to="/widgets/todo-list">
									Task lists
								</StagedLink>{' '}
								per project, a{' '}
								<StagedLink to="/widgets/calendar">
									calendar
								</StagedLink>
								,{' '}
								<StagedLink to="/widgets/notes">
									notes
								</StagedLink>{' '}
								for the things you keep retyping, and{' '}
								<StagedLink to="/widgets/link">
									links
								</StagedLink>{' '}
								placed where you actually reach for them. Opens
								instantly, because there’s nothing to load.
							</p>
						</div>

						<div className={s.column}>
							<h3>A vision board</h3>
							<p>
								<StagedLink to="/widgets/image">
									Photos and GIFs
								</StagedLink>{' '}
								of what you’re working towards, a{' '}
								<StagedLink to="/widgets/habit-tracker">
									habit tracker
								</StagedLink>{' '}
								for the work itself, a{' '}
								<StagedLink to="/widgets/quotes">
									quote
								</StagedLink>{' '}
								to keep you honest. The most reliably seen wall
								you own is the one inside your browser.
							</p>
						</div>
					</div>

					<h2>How it works</h2>

					<p>
						Install it and your next new tab offers you a starting
						template, or skip that and begin on an empty page. The
						button in the bottom-left corner opens the widget panel,
						a click adds a widget, and you drag it wherever you
						like. Double-click to edit what’s inside, drag a corner
						to resize, Ctrl+Z when it all goes wrong. No account, no
						setup, no tutorial to sit through.
					</p>

					<p>
						Your page is stored in your browser, on your machine.
						The layout, your task lists, your notes and your
						countdown dates stay there. They aren’t uploaded to us,
						and there’s no sync service holding a copy. The
						trade-off is worth knowing before you start: clearing
						your browser data clears your page, and nothing moves
						between computers on its own. To move a page, publish it
						as a theme and install that theme on the other machine.
					</p>

					<h2>Free and PRO</h2>

					<p>
						Free gets you three widgets added by hand, every widget
						type, and no trial clock ticking. The bit people miss:
						widgets that arrive with an installed{' '}
						<Link to="/themes">theme</Link> don’t count at all, so a
						ready-made page carrying a dozen of them is free.
						Building from scratch is what costs. Three{' '}
						<StagedLink to="/widgets/workspaces">
							workspaces
						</StagedLink>{' '}
						are included as well.
					</p>

					<p>
						<Link to="/pro">PRO</Link> removes the widget limit,
						allows unlimited{' '}
						<StagedLink to="/widgets/habit-tracker">
							habit trackers
						</StagedLink>
						, changes how many{' '}
						<StagedLink to="/widgets/workspaces">
							workspaces
						</StagedLink>{' '}
						you can have, and unlocks daily and weekly{' '}
						<StagedLink to="/widgets/countdown">
							countdown repeats
						</StagedLink>
						, the workday countdown and timezones on the{' '}
						<StagedLink to="/widgets/analog-clock">
							analog clock
						</StagedLink>
						.
					</p>

					{reviews.length > 0 && (
						<React.Fragment>
							<h2>What people say about it</h2>

							<ul className={s.reviews}>
								{reviews.map(review => (
									<li className={s.review} key={review.text}>
										<div className={s.reviewInner}>
											<p className={s.reviewText}>
												&ldquo;{review.text}&rdquo;
											</p>
											<div className={s.reviewMeta}>
												{review.author}
												{review.date && (
													<React.Fragment>
														{' · '}
														<time
															dateTime={
																review.date
															}
														>
															{formatReviewDate(
																review.date,
															)}
														</time>
													</React.Fragment>
												)}
											</div>
										</div>
									</li>
								))}
							</ul>

							<p className={s.reviewsSource}>
								Left on the{' '}
								<a href={CHROME_STORE_URL}>Chrome Web Store</a>,
								quoted as written. The extension holds{' '}
								{STORE_STATS.ratingValue} out of 5 across{' '}
								{STORE_STATS.ratingCount.toLocaleString(
									'en-US',
								)}{' '}
								ratings there, including the critical ones,
								which are worth a read too.
							</p>
						</React.Fragment>
					)}

					<h2>Questions people ask</h2>

					<div className={s.faq}>
						<div className={s.faqItem}>
							<h3>
								What is the best new tab extension for Chrome
								with widgets?
							</h3>
							<p>
								It depends on what the page is for. Momentum and
								Bonjourr give you a calm photo with the time,
								Infinity New Tab and Speed Dial 2 are grids of
								site icons, and Tabliss mixes a few simple
								blocks. New Tab Studio is the one for a page
								built from widgets you place yourself:
								countdowns, to-do lists, habit trackers, notes,
								a calendar, {WIDGET_TYPES} widgets in all.
							</p>
						</div>

						<div className={s.faqItem}>
							<h3>Is New Tab Studio free?</h3>
							<p>
								Yes, installing it costs nothing and every
								widget type is on the free plan. You can add
								three widgets by hand, and widgets that arrive
								with an installed theme don’t count, so a
								ready-made page full of them stays free.{' '}
								<Link to="/pro">PRO</Link> lifts the
								three-widget limit and unlocks a few extras such
								as daily countdown repeats.
							</p>
						</div>

						<div className={s.faqItem}>
							<h3>
								Is New Tab Studio safe, and what permissions
								does it ask for?
							</h3>
							<p>
								New Tab Studio keeps your page, tasks and notes
								in your browser and works without an account. It
								asks for your bookmarks or most visited sites
								only when you add a widget that shows them. It
								never touches your search engine, and the usage
								statistics it sends don’t include anything you
								type.
							</p>
						</div>

						<div className={s.faqItem}>
							<h3>Does it work in Edge, Brave and Firefox?</h3>
							<p>
								Yes. Edge, Brave, Opera and Vivaldi install it
								from the Chrome Web Store like any Chrome
								extension (Opera needs its Install Chrome
								Extensions add-on first), and Firefox gets its
								own version on Firefox Add-ons. Whatever you
								build stays in the browser where you built it.
							</p>
						</div>

						<div className={s.faqItem}>
							<h3>Can I move my page to another computer?</h3>
							<p>
								Not automatically: there’s no sync, because the
								page lives in one browser’s storage. Export it
								as a file under Settings → Import / export and
								open that file on the other machine, or publish
								the page as a theme and install it there in one
								click.
							</p>
						</div>
					</div>

					<h2>Themes made by other people</h2>

					<p>
						Any page you build can go into the{' '}
						<Link to="/themes">theme gallery</Link>, and anything in
						it installs in one click, wallpaper, widgets, positions,
						styling. Easily the fastest start: find something close
						to what you want, then swap in your own dates and lists.
					</p>
				</div>
			</div>
		);
	}
}

export default withStyles(s)(Home);
